import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import Icon from '../shared/Icon';
import EnhancedLineChart from '../shared/EnhancedLineChart';
import InsightsModule from './InsightsModule';
import Skeleton, { SKELETON_CSS } from '../shared/Skeleton';
import LockedFeature from '../shared/LockedFeature';
import { usePlan } from '../../contexts/PlanContext';
import { useLanguage } from '../../contexts/LanguageContext';

// ── Monochrome corporate palette ────────────────────────────────────────────
const P = {
  blue:   '#0A0A0A',
  green:  '#16A34A',
  purple: '#525252',
  orange: '#8C8C8C',
  red:    '#DC2626',
  gray:   '#6B6B6B',
  dark:   '#0A0A0A',
  border: 'rgba(0,0,0,0.08)',
  bg:     '#FFFFFF',
};

const FONT = "'abcNormal','Inter','Arial',sans-serif";
const FONT_UI = "'Inter',system-ui,sans-serif";

const CSS = `
  @font-face {
    font-family: 'abcNormal';
    src: url('/fonts/ABCNormal_Normal.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  .ap-card{background:#fff;border-radius:12px;border:1px solid #E5E5E5;box-shadow:0 1px 2px rgba(0,0,0,0.04);transition:box-shadow .22s ease,border-color .22s ease;}
  .ap-card:hover{box-shadow:0 2px 8px rgba(0,0,0,0.06);border-color:#D4D4D4;}
  .ap-seg{display:flex;background:#F5F5F5;border-radius:8px;padding:3px;gap:2px;}
  .ap-seg-btn{border:none;border-radius:6px;padding:5px 12px;font-size:12px;font-weight:500;cursor:pointer;transition:background .15s,color .15s,box-shadow .15s;font-family:${FONT_UI};letter-spacing:-.01em;white-space:nowrap;}
  .ap-seg-btn.on{background:#0A0A0A;color:#fff;box-shadow:none;}
  .ap-seg-btn:not(.on){background:transparent;color:${P.gray};}
  .ap-seg-btn:not(.on):hover{color:${P.dark};}
  .ap-icon{width:36px;height:36px;border-radius:8px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
  .ap-num{font-size:30px;font-weight:700;color:${P.dark};letter-spacing:-0.04em;line-height:1;margin:14px 0 3px;font-family:${FONT_UI};}
  .ap-lbl{font-size:13px;color:${P.dark};font-weight:500;letter-spacing:-.01em;}
  .ap-sub{font-size:11px;color:#9A9A9A;margin-top:2px;letter-spacing:-.01em;}
  .ap-bar{height:5px;border-radius:99px;background:#F0F0F0;overflow:hidden;}
  .ap-bar-fill{height:100%;border-radius:99px;}
  .ap-export{display:flex;align-items:center;gap:6px;padding:7px 14px;border:1px solid #D4D4D4;border-radius:8px;background:#fff;color:${P.dark};font-size:12.5px;font-weight:500;cursor:pointer;transition:background .15s,border-color .15s;font-family:${FONT_UI};letter-spacing:-.01em;}
  .ap-export:hover{background:#FAFAFA;border-color:#B0B0B0;}
  .ap-export:disabled{opacity:.35;cursor:not-allowed;}
  .ap-sec{font-size:14px;font-weight:600;color:${P.dark};letter-spacing:-.02em;margin-bottom:14px;font-family:${FONT_UI};}
  @keyframes ap-fadeup{from{opacity:0;transform:translateY(7px)}to{opacity:1;transform:translateY(0)}}
  .ap-enter{animation:ap-fadeup .28s cubic-bezier(.25,.46,.45,.94) both;}
  .ap-enter-1{animation-delay:.04s} .ap-enter-2{animation-delay:.08s} .ap-enter-3{animation-delay:.12s} .ap-enter-4{animation-delay:.16s}

  /* First-visit onboarding (popup) */
  .ap-onboarding-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.45);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;z-index:150;padding:16px;}
  .ap-onboarding-modal{background:#fff;border-radius:20px;box-shadow:0 24px 60px rgba(0,0,0,0.18);max-width:380px;width:100%;padding:30px 26px;text-align:center;font-family:${FONT_UI};}
  .ap-onboarding-icon{width:46px;height:46px;border-radius:13px;background:rgba(0,0,0,0.05);display:flex;align-items:center;justify-content:center;margin:0 auto 16px;color:${P.dark};}
  .ap-onboarding-title{font-size:17px;font-weight:600;color:${P.dark};margin:0 0 8px;letter-spacing:-0.01em;}
  .ap-onboarding-desc{font-size:13.5px;color:${P.gray};line-height:1.55;margin:0 0 22px;}
  .ap-onboarding-close{width:100%;height:40px;border:none;border-radius:980px;background:#0A0A0A;color:#fff;font-size:13.5px;font-weight:500;cursor:pointer;font-family:${FONT_UI};transition:opacity .15s;}
  .ap-onboarding-close:hover{opacity:.86;}

  @media (max-width: 768px) {
    .ap-card{padding:13px 15px !important;}
    .ap-num{font-size:21px;margin:9px 0 2px;}
    .ap-lbl{font-size:10.5px;}
    .ap-sub{font-size:10.5px;}
    .ap-sec{font-size:11px;margin-bottom:9px;}
    .ap-seg-btn{font-size:10.5px;padding:3px 7px;}
    .ap-icon{width:24px;height:24px;}
    .ap-export{padding:5px 9px;font-size:10.5px;gap:4px;}
    .ap-bar{height:3.5px;}
    .ap-most-effective-icon{width:44px !important;height:44px !important;}
    .ap-most-effective-icon-inner{width:22px !important;height:22px !important;}
    .ap-most-effective-name{font-size:15px !important;}
    .ap-most-effective-pct{font-size:26px !important;}
    .ap-social-icon-wrap{width:26px !important;height:26px !important;}
    .ap-social-icon-inner{width:13px !important;height:13px !important;}
    .ap-social-clicks{font-size:13px !important;}
  }
`;

// Metadata de plataformas con colores de marca
const PLATFORMS = {
  whatsapp:  { name: 'WhatsApp',  color: '#25d366', icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg> },
  instagram: { name: 'Instagram', color: '#e1306c', icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg> },
  linkedin:  { name: 'LinkedIn',  color: '#0a66c2', icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> },
  facebook:  { name: 'Facebook',  color: '#1877f2', icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
  email:     { name: 'Email',     color: '#6e6e73', icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg> },
  website:   { name: 'Sitio Web', color: '#1d1d1f', icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg> },
  twitter:   { name: 'Twitter',   color: '#1da1f2', icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg> },
};

const METRICS = [
  { key: 'total_visits',        label: 'Visitas Totales',      sub: 'Impacto total',            color: P.dark, icon: 'eye'     },
  { key: 'social_clicks_total', label: 'Clics en Redes',       sub: 'Interacciones sociales',   color: P.dark, icon: 'click'   },
  { key: 'vcf_downloads',       label: 'Contactos Guardados',  sub: 'Descargas VCF',            color: P.dark, icon: 'phone'   },
  { key: 'engagement_rate',     label: 'Tasa de Interacción',  sub: 'Engagement promedio',      color: P.dark, icon: 'trending', suffix: '%' },
];

// 'website' is the one PLATFORMS entry with a Spanish display name — every
// other key is a proper noun (WhatsApp, Instagram...) so it's the same in
// both languages and doesn't need translating.
const platformDisplayName = (key, cfg, t) => key === 'website' ? t('analytics.platform.website', 'Sitio Web') : cfg.name;

// ── Pie Chart ───────────────────────────────────────────────────────────────
function ptoc(cx, cy, r, deg) {
  const rad = (deg - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function slice(cx, cy, r, a0, a1) {
  const end = Math.min(a1, a0 + 359.99);
  const p1 = ptoc(cx, cy, r, a0);
  const p2 = ptoc(cx, cy, r, end);
  const lg = end - a0 > 180 ? 1 : 0;
  return `M${cx},${cy} L${p1.x.toFixed(2)},${p1.y.toFixed(2)} A${r},${r} 0 ${lg} 1 ${p2.x.toFixed(2)},${p2.y.toFixed(2)}Z`;
}

const EngagementPie = ({ data }) => {
  const { t } = useLanguage();
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered]   = useState(null);

  const total = (data.social_clicks_total || 0) + (data.vcf_downloads || 0);
  if (total === 0) {
    return (
      <div style={{textAlign:'center',paddingTop:12,paddingBottom:8}}>
        <div style={{width:44,height:44,borderRadius:12,background:'rgba(0,0,0,0.04)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 12px'}}>
          <Icon name="chart" className="w-5 h-5" style={{color:P.gray}}/>
        </div>
        <p style={{fontSize:13,fontWeight:500,color:P.dark,margin:'0 0 4px'}}>{t('analytics.noData', 'Sin datos suficientes')}</p>
        <p style={{fontSize:12,color:P.gray,margin:0}}>{t('analytics.pie.emptyDesc', 'Aparecerá aquí cuando tengas visitas o contactos guardados')}</p>
      </div>
    );
  }

  const segs = [];
  Object.entries(data.social_clicks || {}).forEach(([k, v]) => {
    if (v > 0) {
      const cfg = PLATFORMS[k] || { name: k.charAt(0).toUpperCase() + k.slice(1), color: P.gray };
      segs.push({ key: k, label: platformDisplayName(k, cfg, t), value: v, color: cfg.color });
    }
  });
  if ((data.vcf_downloads || 0) > 0)
    segs.push({ key: 'vcf', label: t('analytics.pie.saved', 'Guardados'), value: data.vcf_downloads, color: P.purple });
  segs.sort((a, b) => b.value - a.value);

  const cx = 100, cy = 100, r = 90, GAP = 1.2;
  let a = 0;
  const arcs = segs.map(s => {
    const pct   = s.value / total;
    const sw    = pct * 360;
    const gap   = sw > 5 ? GAP : 0;
    const a0    = a + gap / 2;
    const a1    = a + sw - gap / 2;
    const midDeg = a0 + (a1 - a0) / 2;
    const midRad = (midDeg - 90) * Math.PI / 180;
    const lp    = ptoc(cx, cy, r * 0.61, midDeg);
    const dx    = Math.cos(midRad) * 9;
    const dy    = Math.sin(midRad) * 9;
    a += sw;
    return { ...s, pct, a0, a1, sw, lp, dx, dy };
  });

  const active = hovered ?? selected;

  return (
    <div>
      {/* ── Pie SVG ── */}
      <svg
        viewBox="-10 -10 220 220"
        width="100%"
        style={{ display: 'block', maxHeight: 210, overflow: 'visible' }}
      >
        {arcs.map(s => {
          const on = active === s.key;
          const tx = on ? s.dx.toFixed(2) : 0;
          const ty = on ? s.dy.toFixed(2) : 0;
          return (
            <g
              key={s.key}
              transform={`translate(${tx},${ty})`}
              style={{ cursor: 'pointer', transition: 'transform .26s cubic-bezier(.34,1.56,.64,1)' }}
              onClick={() => setSelected(selected === s.key ? null : s.key)}
              onMouseEnter={() => setHovered(s.key)}
              onMouseLeave={() => setHovered(null)}
            >
              <path
                d={slice(cx, cy, r, s.a0, s.a1)}
                fill={s.color}
                opacity={active !== null && !on ? 0.28 : 1}
                style={{ transition: 'opacity .18s ease, filter .18s ease', filter: on ? 'brightness(1.1)' : 'none' }}
              />
              {s.sw > 16 && (
                <text
                  x={s.lp.x} y={s.lp.y + 4}
                  textAnchor="middle"
                  fill="rgba(255,255,255,0.88)"
                  fontSize={9} fontWeight={500}
                  fontFamily="'Inter',system-ui,sans-serif"
                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                >
                  {s.value.toLocaleString()}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* ── Legend ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 8 }}>
        {arcs.map(s => {
          const isSel = selected === s.key;
          const isHov = hovered  === s.key;
          return (
            <div
              key={s.key}
              onClick={() => setSelected(selected === s.key ? null : s.key)}
              onMouseEnter={() => setHovered(s.key)}
              onMouseLeave={() => setHovered(null)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '5px 9px', borderRadius: 9,
                background: isSel ? `${s.color}12` : isHov ? `${s.color}08` : 'transparent',
                border: `1px solid ${isSel ? s.color + '30' : 'transparent'}`,
                cursor: 'pointer',
                transition: 'background .16s, border-color .16s',
              }}
            >
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, flexShrink: 0,
                boxShadow: isHov || isSel ? `0 0 0 3px ${s.color}28` : 'none',
                transition: 'box-shadow .18s',
              }} />
              <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: P.dark, letterSpacing: '-0.01em',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {s.label}
              </span>
              <span style={{ fontSize: 11, color: '#aeaeb2', marginRight: 2 }}>
                {s.value.toLocaleString()}
              </span>
              <span style={{ fontSize: 11, fontWeight: 600, color: s.color, minWidth: 38, textAlign: 'right' }}>
                {(s.pct * 100).toFixed(1)}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AnalyticsPanel = ({ analytics, profile, monthlyComparison, user }) => {
  const { hasFeature } = usePlan();
  const { t } = useLanguage();
  const onboardingKey = user?.id ? `analytics_onboarding_seen_${user.id}` : null;
  const [showOnboarding, setShowOnboarding] = useState(
    () => !!onboardingKey && !localStorage.getItem(onboardingKey)
  );
  const dismissOnboarding = () => {
    if (onboardingKey) localStorage.setItem(onboardingKey, '1');
    setShowOnboarding(false);
  };
  const [globalDateRange, setGlobalDateRange] = useState('all-time');
  const [unifiedData, setUnifiedData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (profile) loadUnifiedAnalytics(profile.id, globalDateRange);
  }, [profile, globalDateRange]);

  const loadUnifiedAnalytics = async (profileId, dateRange) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/${profileId}/unified?date_range=${dateRange}`);
      setUnifiedData(res.data);
    } catch (e) {
      console.error('Error loading unified analytics:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const res = await axios.get(`${API}/analytics/${profile.id}/executive-report?date_range=${globalDateRange}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('analytics.exportFilename', 'Resumen_Ejecutivo')}_${globalDateRange.replace('-','_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error downloading report:', e);
    }
  };

  const dateRangeOptions = [
    { value: '7d',       label: '7D'     },
    { value: '1m',       label: '1M'     },
    { value: '3m',       label: '3M'     },
    { value: '6m',       label: '6M'     },
    { value: '12m',      label: '12M'    },
    { value: 'all-time', label: t('analytics.range.all', 'Todo')   },
  ];

  if (!profile) return (
    <div style={{textAlign:'center',padding:'48px 0'}}>
      <p style={{color:P.gray,fontSize:14}}>{t('analytics.selectProfile', 'Selecciona un perfil para ver las analíticas')}</p>
    </div>
  );

  return (
    <div style={{maxWidth:'100%',width:'100%',overflow:'hidden'}}>
      <style>{CSS}</style>

      {/* First-visit onboarding (popup) */}
      {showOnboarding && (
        <div className="ap-onboarding-overlay" onClick={dismissOnboarding}>
          <div className="ap-onboarding-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ap-onboarding-icon">
              <Icon name="chart" className="w-5 h-5" />
            </div>
            <p className="ap-onboarding-title">{t('analytics.onboarding.title', 'Bienvenido a Analytics')}</p>
            <p className="ap-onboarding-desc">
              {t('analytics.onboarding.desc', 'Aquí puedes ver cuántas personas visitan tu perfil, qué redes sociales generan más clics y cómo evoluciona tu actividad en el tiempo. Los números se actualizan solos a medida que llegan visitas.')}
            </p>
            <button className="ap-onboarding-close" onClick={dismissOnboarding}>{t('leads.onboarding.close', 'Entendido')}</button>
          </div>
        </div>
      )}

      {/* ── Header ──────────────────────────────────────────────────── */}
      <div style={{marginBottom:24,paddingBottom:20,borderBottom:`1px solid ${P.border}`}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:16}}>
          <div>
            <h1 style={{fontSize:28,fontWeight:300,color:P.dark,letterSpacing:'-0.02em',margin:0,fontFamily:FONT}}>
              Analytics
            </h1>
            <p style={{fontSize:13,color:P.gray,margin:'6px 0 0',letterSpacing:'-0.01em',fontFamily:FONT_UI}}>
              {unifiedData?.profile_name || profile.name} · /pages/{profile.slug}
            </p>
          </div>

          <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
            <div className="ap-seg">
              {dateRangeOptions.map(o => (
                <button
                  key={o.value}
                  className={`ap-seg-btn${globalDateRange === o.value ? ' on' : ''}`}
                  onClick={() => setGlobalDateRange(o.value)}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {hasFeature('exports') && (
              <button
                className="ap-export"
                onClick={handleExportExcel}
                disabled={loading || !unifiedData}
              >
                <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                {t('analytics.export', 'Exportar')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Loading ──────────────────────────────────────────────────── */}
      {loading && (
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-4">
          <style>{SKELETON_CSS}</style>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="col-span-1 lg:col-span-3">
              <div className="ap-card" style={{ padding: '20px 22px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                  <Skeleton width={36} height={36} radius={8} />
                  <div style={{ flex: 1 }}>
                    <Skeleton width="70%" height={13} style={{ marginBottom: 6 }} />
                    <Skeleton width="45%" height={11} />
                  </div>
                </div>
                <Skeleton width="40%" height={26} />
              </div>
            </div>
          ))}

          <div className="col-span-2 lg:col-span-8" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="ap-card" style={{ padding: '20px 24px' }}>
              <Skeleton width={160} height={14} style={{ marginBottom: 18 }} />
              <Skeleton width="100%" height={220} radius={10} />
            </div>
            <div className="ap-card" style={{ padding: '20px 24px' }}>
              <Skeleton width={200} height={14} style={{ marginBottom: 18 }} />
              {[1, 2, 3, 4].map((i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px' }}>
                  <Skeleton width={30} height={30} radius={8} />
                  <Skeleton width="60%" height={12} />
                </div>
              ))}
            </div>
          </div>

          <div className="col-span-2 lg:col-span-4" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="ap-card" style={{ padding: '20px 24px' }}>
              <Skeleton width={180} height={14} style={{ marginBottom: 18 }} />
              <Skeleton width={180} height={180} radius={90} style={{ margin: '0 auto' }} />
            </div>
            <div className="ap-card" style={{ padding: '20px 24px' }}>
              <Skeleton width={140} height={14} style={{ marginBottom: 18 }} />
              <Skeleton width="100%" height={60} radius={10} />
            </div>
          </div>
        </div>
      )}

      {unifiedData && !loading && (
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-4">

          {/* ── Metric cards ──────────────────────────────────────────── */}
          {METRICS.map((m, i) => (
            <div key={m.key} className={`col-span-1 lg:col-span-3 ap-enter ap-enter-${i+1}`}>
              <div className="ap-card" style={{padding:'20px 22px'}}>
                <div style={{display:'flex',alignItems:'center',gap:14,marginBottom:12}}>
                  <div className="ap-icon" style={{background:`${m.color}12`,color:m.color}}>
                    <Icon name={m.icon} className="w-4 h-4" />
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div className="ap-lbl">{t(`analytics.metric.${m.key}.label`, m.label)}</div>
                    <div className="ap-sub">{t(`analytics.metric.${m.key}.sub`, m.sub)}</div>
                  </div>
                </div>
                <div className="ap-num">{unifiedData[m.key]}{m.suffix || ''}</div>
              </div>
            </div>
          ))}

          {/* ── Everything past the 4 headline KPIs is Pro-only ─────────── */}
          <div className="col-span-2 lg:col-span-12">
          <LockedFeature feature="analytics">
          <div className="grid grid-cols-2 lg:grid-cols-12 gap-4">

          {/* ── Left column: Tendencia Temporal + Rendimiento por Red Social ── */}
          <div className="col-span-2 lg:col-span-8" style={{display:'flex',flexDirection:'column',gap:16}}>

            {/* ── Temporal chart ──────────────────────────────────────── */}
            <div className="ap-card" style={{padding:'20px 24px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
                <h3 className="ap-sec" style={{margin:0}}>{t('analytics.trend.title', 'Tendencia Temporal')}</h3>
                <span style={{fontSize:12,color:P.gray}}>
                  {globalDateRange === 'all-time'
                    ? t('analytics.trend.allTime', 'Toda la vida') : dateRangeOptions.find(o => o.value === globalDateRange)?.label}
                </span>
              </div>
              <EnhancedLineChart
                data={unifiedData.temporal_data || []}
                period={globalDateRange}
                timeUnit={unifiedData.time_unit}
              />
            </div>

            {/* ── Social performance ─────────────────────────────────── */}
            <div className="ap-card" style={{padding:'20px 24px'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:18}}>
                <h3 className="ap-sec" style={{margin:0}}>{t('analytics.social.title', 'Rendimiento por Red Social')}</h3>
                {unifiedData.social_clicks_total > 0 && (
                  <span style={{fontSize:12,color:P.gray,background:'rgba(0,0,0,0.04)',borderRadius:980,padding:'3px 10px',fontWeight:500}}>
                    {unifiedData.social_clicks_total} {t('analytics.social.totalClicks', 'clics totales')}
                  </span>
                )}
              </div>
              {(() => {
                const sorted = Object.entries(unifiedData.social_clicks || {})
                  .sort(([,a],[,b]) => b - a);
                const max = sorted[0]?.[1] || 1;
                if (sorted.length === 0) {
                  return (
                    <div style={{textAlign:'center',paddingTop:12,paddingBottom:8}}>
                      <div style={{width:44,height:44,borderRadius:12,background:'rgba(0,0,0,0.04)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 12px'}}>
                        <Icon name="plug" className="w-5 h-5" style={{color:P.gray}}/>
                      </div>
                      <p style={{fontSize:13,fontWeight:500,color:P.dark,margin:'0 0 4px'}}>{t('analytics.social.emptyTitle', 'Aún no tienes clics')}</p>
                      <p style={{fontSize:12,color:P.gray,margin:0}}>{t('analytics.social.emptyDesc', 'Aparecerán aquí cuando alguien haga clic en tus redes sociales')}</p>
                    </div>
                  );
                }
                return (
                  <div style={{display:'flex',flexDirection:'column',gap:4}}>
                    {sorted.map(([platform, clicks], idx) => {
                      const pct = unifiedData.social_clicks_total > 0
                        ? (clicks / unifiedData.social_clicks_total) * 100 : 0;
                      const barW = (clicks / max) * 100;
                      const cfg = PLATFORMS[platform] || {
                        name: platform.charAt(0).toUpperCase() + platform.slice(1),
                        color: P.gray,
                        icon: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 7h-2v4H7v2h4v4h2v-4h4v-2h-4V7zm-1-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>
                      };
                      return (
                        <div key={platform} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 12px',borderRadius:10,transition:'background .15s'}}
                          onMouseEnter={e=>e.currentTarget.style.background='rgba(0,0,0,0.03)'}
                          onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                        >
                          <span style={{fontSize:11,fontWeight:700,color:idx===0?cfg.color:'#aeaeb2',width:16,textAlign:'center',flexShrink:0,letterSpacing:0}}>
                            {idx+1}
                          </span>
                          <div className="ap-social-icon-wrap" style={{width:30,height:30,borderRadius:8,background:`${cfg.color}18`,display:'flex',alignItems:'center',justifyContent:'center',color:cfg.color,flexShrink:0}}>
                            <div className="ap-social-icon-inner" style={{width:15,height:15}}>{cfg.icon}</div>
                          </div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:13,fontWeight:500,color:P.dark,letterSpacing:'-0.01em',marginBottom:5}}>
                              {platformDisplayName(platform, cfg, t)}
                            </div>
                            <div className="ap-bar">
                              <div className="ap-bar-fill" style={{width:`${barW}%`,background:cfg.color,transition:'width .4s ease'}}/>
                            </div>
                          </div>
                          <div style={{textAlign:'right',flexShrink:0}}>
                            <div className="ap-social-clicks" style={{fontSize:15,fontWeight:700,color:P.dark,letterSpacing:'-0.02em',lineHeight:1}}>{clicks}</div>
                            <div style={{fontSize:11,color:P.gray,marginTop:2}}>{pct.toFixed(1)}%</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* ── Right column: Distribución de Interacciones + Red Más Efectiva ── */}
          <div className="col-span-2 lg:col-span-4" style={{display:'flex',flexDirection:'column',gap:16}}>

            {/* ── Engagement Pie ───────────────────────────────────────── */}
            <div className="ap-card" style={{padding:'20px 24px'}}>
              <h3 className="ap-sec">{t('analytics.pie.title', 'Distribución de Interacciones')}</h3>
              <EngagementPie data={unifiedData} />
            </div>

            {/* ── Most effective ──────────────────────────────────────── */}
            <div className="ap-card" style={{padding:'20px 24px'}}>
              <h3 className="ap-sec">{t('analytics.mostEffective.title', 'Red Más Efectiva')}</h3>
              {(() => {
                const entries = Object.entries(unifiedData.social_clicks || {}).filter(([,v]) => v > 0);
                const [topPlatform, topClicks] = entries.sort(([,a],[,b]) => b - a)[0] || [null, 0];
                const topPct = unifiedData.social_clicks_total > 0
                  ? ((topClicks / unifiedData.social_clicks_total) * 100).toFixed(1)
                  : null;
                const cfg = topPlatform
                  ? (PLATFORMS[topPlatform] || { name: topPlatform.charAt(0).toUpperCase() + topPlatform.slice(1), color: P.blue, icon: null })
                  : null;
                return cfg ? (
                  <div style={{display:'flex',flexDirection:'column',alignItems:'center',textAlign:'center',paddingTop:12}}>
                    <div className="ap-most-effective-icon" style={{width:52,height:52,borderRadius:14,background:`${cfg.color}18`,display:'flex',alignItems:'center',justifyContent:'center',color:cfg.color,marginBottom:14}}>
                      <div className="ap-most-effective-icon-inner" style={{width:26,height:26}}>{cfg.icon}</div>
                    </div>
                    <div className="ap-most-effective-name" style={{fontSize:18,fontWeight:700,color:P.dark,letterSpacing:'-0.02em',marginBottom:6}}>
                      {platformDisplayName(topPlatform, cfg, t)}
                    </div>
                    <div className="ap-most-effective-pct" style={{fontSize:32,fontWeight:700,color:cfg.color,letterSpacing:'-0.04em',lineHeight:1}}>
                      {topPct}%
                    </div>
                    <div style={{fontSize:12,color:P.gray,marginTop:6}}>{t('analytics.mostEffective.suffix', 'de interacciones sociales')}</div>
                  </div>
                ) : (
                  <div style={{textAlign:'center',paddingTop:24}}>
                    <div style={{width:44,height:44,borderRadius:12,background:'rgba(0,0,0,0.04)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 12px'}}>
                      <Icon name="chart" className="w-5 h-5" style={{color:P.gray}}/>
                    </div>
                    <p style={{fontSize:13,fontWeight:500,color:P.dark,margin:'0 0 4px'}}>{t('analytics.noData', 'Sin datos suficientes')}</p>
                    <p style={{fontSize:12,color:P.gray,margin:0}}>{t('analytics.mostEffective.emptyDesc', 'Prueba con un período más amplio o espera a que lleguen más visitas')}</p>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* ── Insights — full width ────────────────────────────────── */}
          <div className="col-span-2 lg:col-span-12">
            <InsightsModule profile={profile} dateRange={globalDateRange} />
          </div>

          </div>
          </LockedFeature>
          </div>

        </div>
      )}
    </div>
  );
};

export default AnalyticsPanel;
